// B1 (1. 10. 2026): a timer that reaches 0 changes the screen without a reload. Energy and camp HP are
// worked out on the device from what /me sent, with the same shared rule as the server (`energyNow`), so
// the number moves the second the countdown ends. Every other countdown (a craft, a building, the crew,
// a run coming back, the Trader's new offers, the contracts' reset, the daily reward, the horde) is a
// time in some cached answer: when the earliest of them passes, the answers that hold it are asked for
// again (twice, as the server's jobs run every few seconds), and only those: asking for every screen at
// every deadline went over the server's 240 requests a minute in a fast test game. Energy and HP need no
// new answer (they are worked out here). Coming back to the game (`visibilitychange`) asks for
// everything again.

import { useEffect, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { energyFullAt, energyMax, energyNow, energyRegenMinutes, type MeDTO, type SurvivorSummaryDTO } from '@lastday/shared';
import { serverNow } from './format';

// ---- A tick that moves only when a deadline passes ------------------------------------------------

let tick = 0;
const subs = new Set<() => void>();
const bump = () => {
  tick++;
  for (const f of subs) f();
};
const subscribe = (f: () => void) => {
  subs.add(f);
  return () => subs.delete(f);
};

/** Changes whenever a watched deadline has passed (or the game came back from the background). */
export function useLiveTick(): number {
  return useSyncExternalStore(subscribe, () => tick);
}

// ---- Energy and HP now ----------------------------------------------------------------------------

/**
 * The survivor as it is now: energy refilled since /me answered (the shared `energyNow`, from the point
 * that was under way: `energyNextAt` minus one period), and camp HP full once its time has come.
 */
export function liveSurvivor(s: SurvivorSummaryDTO, now: number, speed: number): SurvivorSummaryDTO {
  let out = s;
  if (s.energyNextAt != null && now >= s.energyNextAt) {
    // 7. 10. 2026 (owner, energy test): the same context as the server (the Medical Tent, the survivor's start).
    const ctx = s.energyCtx ?? {};
    const period = (energyRegenMinutes(s.level, s.energyNextAt - 1, ctx) * 60_000) / Math.max(1, speed);
    const e = energyNow(s.energy, s.energyNextAt - period, now, s.level, speed, ctx);
    const full = e.energy >= energyMax(s.level, ctx);
    out = { ...out, energy: e.energy, energyMax: energyMax(s.level, ctx), energyNextAt: full ? null : now + e.nextInMs, energyFullAt: full ? null : (energyFullAt(e.energy, e.updatedAt, now, s.level, speed, ctx) ?? s.energyFullAt) };
  }
  if (s.hpFullAt != null && now >= s.hpFullAt) out = { ...out, hp: s.hpMax, hpFullAt: null };
  return out;
}

/** `/me` with the survivor as it is now (the `select` of useMe; `tick` only makes it run again). */
export function liveMe(me: MeDTO, _tick: number): MeDTO {
  if (!me.survivor) return me;
  const s = liveSurvivor(me.survivor, serverNow(), me.features.gameSpeed ?? 1);
  return s === me.survivor ? me : { ...me, survivor: s };
}

// ---- The earliest deadline in the cache ----------------------------------------------------------

const WINDOW = 48 * 3_600_000;
const TIME_KEY = /(At|Until)$/;
/** Worked out on the device (liveSurvivor): at these the tick moves, no answer is asked for again. */
const LOCAL_KEYS = new Set(['energyNextAt', 'energyFullAt', 'hpFullAt']);
/** Deadlines this close to the earliest one are handled together. */
const TOGETHER = 1_000;

export interface Deadline {
  at: number;
  /** The cached answers that hold a deadline at that moment (empty: energy or HP only). */
  queries: QueryKey[];
}

/**
 * The earliest future time (ms) in the cached answers, fields named `…At` or `…Until` within 48 hours,
 * and the answers that hold it (or one within a second of it).
 */
export function nextDeadline(qc: QueryClient, now: number): Deadline | null {
  const found: { at: number; key: QueryKey; local: boolean }[] = [];
  for (const q of qc.getQueryCache().getAll()) {
    const seen = new Set<unknown>();
    const walk = (v: unknown, depth: number) => {
      if (!v || typeof v !== 'object' || depth > 6 || seen.has(v)) return;
      seen.add(v);
      if (Array.isArray(v)) {
        for (const x of v.slice(0, 200)) walk(x, depth + 1);
        return;
      }
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
        if (typeof x === 'number' && TIME_KEY.test(k) && x > now && x < now + WINDOW) found.push({ at: x, key: q.queryKey, local: LOCAL_KEYS.has(k) });
        else if (x && typeof x === 'object') walk(x, depth + 1);
      }
    };
    walk(liveData(q.state.data, now), 0);
  }
  if (!found.length) return null;
  const at = Math.min(...found.map((f) => f.at));
  const keys = new Map<string, QueryKey>();
  for (const f of found) if (!f.local && f.at - at <= TOGETHER) keys.set(JSON.stringify(f.key), f.key);
  return { at, queries: [...keys.values()] };
}

/** /me as it is now: its energy and HP deadlines move on the device, not in the cached answer. */
function liveData(data: unknown, now: number): unknown {
  const me = data as Partial<MeDTO> | undefined;
  if (!me || typeof me !== 'object' || !me.survivor || !me.features) return data;
  return { ...me, survivor: liveSurvivor(me.survivor, now, me.features.gameSpeed ?? 1) };
}

/** Asks these answers again, if a screen shows them (and the tick moves, so energy and HP update at once). */
function refresh(qc: QueryClient, queries: QueryKey[] | 'all') {
  bump();
  if (queries === 'all') void qc.invalidateQueries({ refetchType: 'active' });
  else for (const queryKey of queries) void qc.invalidateQueries({ queryKey, exact: true, refetchType: 'active' });
}

/**
 * Mounted once (Boot): wakes at the earliest deadline in the cache, moves the tick and asks for the data
 * again right after it and a few seconds later (the server's jobs run every 1–5 seconds); and does the same
 * when the game comes back from the background.
 */
export function useDeadlines() {
  const qc = useQueryClient();
  useEffect(() => {
    let wake: ReturnType<typeof setTimeout> | null = null;
    const after: ReturnType<typeof setTimeout>[] = [];
    let armed: string | null = null;
    const arm = () => {
      const now = serverNow();
      const next = nextDeadline(qc, now);
      const sig = next ? `${next.at}|${next.queries.map((k) => JSON.stringify(k)).join(',')}` : null;
      if (sig === armed) return;
      if (wake) clearTimeout(wake);
      wake = null;
      armed = sig;
      if (!next) return;
      wake = setTimeout(
        () => {
          armed = null;
          bump();
          if (next.queries.length) {
            after.push(setTimeout(() => refresh(qc, next.queries), 2_500));
            after.push(setTimeout(() => refresh(qc, next.queries), 8_000));
          }
          arm();
        },
        Math.max(0, next.at - now) + 50,
      );
    };
    const unsub = qc.getQueryCache().subscribe((e) => {
      if (e.type === 'updated' && e.action.type === 'success') arm();
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh(qc, 'all');
        arm();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    arm();
    return () => {
      unsub();
      document.removeEventListener('visibilitychange', onVisible);
      if (wake) clearTimeout(wake);
      for (const t of after) clearTimeout(t);
    };
  }, [qc]);
}
